import React, { useState, useEffect } from 'react';
import { selectorBancoHoras } from '@core/slice/banco-horas.slice';
import { useAppSelector } from '@store/hooks';
import { Avatar, Box, Container, Grid, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { colaboradorService } from '@core/services';
import { formatDate, getDateFromSap, getInitials, isFilled, isObjectEmpty, formatSituation } from '@core/utils';
import { ContentSeparator, DatePicker, LoadingView } from '@core/components';
import { AlertComponent } from '@core/components/atoms/alert-component';
import { TableCellStyled } from '@core/components/organisms/simple-table/styled';

interface IBancoHorasProps {}
export const BancoHoras: React.FC<IBancoHorasProps> = () => {
    const { data, loading, error } = useAppSelector(selectorBancoHoras);
    const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs().startOf('month'));

    useEffect(() => {
        colaboradorService.buscarBancoHoras(dayjs(selectedDate).format('YYYY-MM-DD'));
    }, []);

    const handleDateChange = (newDate: Dayjs | null) => {
        setSelectedDate(newDate);
        colaboradorService.buscarBancoHoras(dayjs(newDate).format('YYYY-MM-DD'));
    };

    const isNegative = (value: any): boolean => {
        let valueToString = typeof value !== 'string' ? value.toString() : value;

        if (/^-/.test(valueToString)) {
            return true;
        }
        return false;
    };

    const sharedFontSize = {
        fontSize: '0.8rem'
    };

    const sharedFontSizeBold = {
        fontSize: '0.8rem',
        fontWeight: 'bold'
    };

    return (
        <Container maxWidth="xl" sx={{ px: '2rem' }}>
            <Grid>
                <DatePicker selectedDate={selectedDate} onDateChange={handleDateChange} />
            </Grid>
            {loading && <LoadingView />}

            {isFilled(error) && !loading ? (
                <AlertComponent type="error" detailed={true} message={error ?? '-'} />
            ) : isObjectEmpty(data) && !loading ? (
                <AlertComponent type="info" detailed={true} message="Pesquise o demonstrativo." />
            ) : !loading ? (
                <>
                    <ContentSeparator title="DADOS DO COLABORADOR" />
                    <Grid container spacing={2}>
                        <Grid item md={6} sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton sx={{ mr: '4px', p: 0 }}>
                                <Avatar sx={{ bgcolor: '#FF00FF' }} alt={data.nome || '-'}>
                                    {getInitials(data.nome) || '-'}
                                </Avatar>
                            </IconButton>
                            <Grid>
                                <Typography style={sharedFontSizeBold}>{data.nome}</Typography>
                                <Typography style={sharedFontSize}>{data.cargo}</Typography>
                            </Grid>
                        </Grid>
                        <Grid item md={4}>
                            <Typography style={sharedFontSizeBold}>CADASTRO</Typography>
                            <Typography style={sharedFontSize}>{data.matricula}</Typography>
                        </Grid>
                    </Grid>
                    <ContentSeparator title="LANÇAMENTOS DO COLABORADOR" />

                    <Grid container spacing={2}>
                        <Grid item md={4}>
                            <Typography style={sharedFontSizeBold}>BANCO DE HORAS</Typography>
                            <Typography style={sharedFontSize}>{data.descricaoBancoHoras}</Typography>
                        </Grid>
                        {/* <Grid item md={4}>
                            <Typography style={sharedFontSizeBold}>CRIAÇÃO DA CONTA</Typography>
                            <Typography style={sharedFontSize}>22/10/2020</Typography>
                        </Grid>
                        <Grid item md={4}>
                            <Typography style={sharedFontSizeBold}>FECHAMENTO DA CONTA</Typography>
                            <Typography style={sharedFontSize}>-</Typography>
                        </Grid> */}
                    </Grid>

                    <Grid container spacing={2} sx={{ marginTop: '2rem' }}>
                        <Grid item md={3}>
                            <Typography style={sharedFontSizeBold}>Saldo inicial</Typography>
                            <Typography style={sharedFontSize} sx={{ color: isNegative(data.saldoInicial) ? 'red' : 'inherit' }}>
                                {data.saldoInicial}
                            </Typography>
                        </Grid>
                        <Grid item md={3}>
                            <Typography style={sharedFontSizeBold}>Créditos</Typography>
                            <Typography style={sharedFontSize}>{data.creditos}</Typography>
                        </Grid>
                        <Grid item md={3}>
                            <Typography style={sharedFontSizeBold}>Débitos</Typography>
                            <Typography style={sharedFontSize} sx={{ color: 'red' }}>
                                {data.debitos}
                            </Typography>
                        </Grid>
                        <Grid item md={3}>
                            <Typography style={sharedFontSizeBold}>Saldo final</Typography>
                            <Typography style={sharedFontSize} sx={{ color: isNegative(data.saldoFinal) ? 'red' : 'inherit' }}>
                                {data.saldoFinal}
                            </Typography>
                        </Grid>
                    </Grid>
                    <ContentSeparator title="EXTRATO DE LANÇAMENTOS" />

                    <Box sx={{ width: '100%', marginTop: '2rem' }}>
                        <TableContainer sx={{ overflowX: 'auto' }} component={Paper}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCellStyled align="left">Data</TableCellStyled>
                                        <TableCellStyled align="left">Situação</TableCellStyled>
                                        {/*<TableCellStyled align="left" sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                                            Origem
                                        </TableCellStyled>*/}
                                        <TableCellStyled align="left">Qtde</TableCellStyled>
                                        <TableCellStyled align="left">Saldo</TableCellStyled>
                                        <TableCellStyled align="left">Limite</TableCellStyled>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.lancamentos ? (
                                        data.lancamentos.map((lancamento, index) => (
                                            <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                                <TableCell style={sharedFontSize} sx={{ whiteSpace: 'nowrap' }} align="left">
                                                    {formatDate(getDateFromSap(lancamento.data), true)}
                                                </TableCell>
                                                <TableCell style={sharedFontSize} sx={{ whiteSpace: 'nowrap' }} align="left">
                                                    {formatSituation(lancamento.situacao)}
                                                </TableCell>
                                                {/*<TableCell style={sharedFontSize} sx={{ whiteSpace: 'nowrap', display: { xs: 'none', md: 'table-cell' } }} align="left">
                                                    {lancamento.origem}
                                                </TableCell>*/}
                                                <TableCell style={sharedFontSize} sx={{ whiteSpace: 'nowrap' }} align="left">
                                                    {lancamento.sinlan}
                                                    {lancamento.qtde}
                                                </TableCell>
                                                <TableCell style={sharedFontSize} sx={{ whiteSpace: 'nowrap' }} align="left">
                                                    {lancamento.saldo}
                                                </TableCell>
                                                <TableCell style={sharedFontSize} sx={{ whiteSpace: 'nowrap' }} align="left">
                                                    {formatDate(getDateFromSap(lancamento.limite), true)}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow key={0} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                            <TableCell style={sharedFontSize} sx={{ whiteSpace: 'nowrap' }} align="left">
                                                Não há lançamentos para exibir.
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Box>
                </>
            ) : (
                ''
            )}
        </Container>
    );
};
